import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { getUtm } from "../lib/utm";
import { SERVICES, BUDGETS } from "../lib/site";

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  business_name: "",
  service: "",
  budget: "",
  message: "",
  website: "",
};

const Field = ({ label, error, children, testId }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-300 mb-1.5">{label}</label>
    {children}
    {error && (
      <p className="text-xs text-red-400 mt-1.5" data-testid={testId}>
        {error}
      </p>
    )}
  </div>
);

export const LeadForm = ({ formId, prefix }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (form.full_name.trim().length < 2) errs.full_name = "Please enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Enter a valid email address";
    if (form.phone.replace(/\D/g, "").length < 10) errs.phone = "Enter a valid 10-digit number";
    if (!form.business_name.trim()) errs.business_name = "Business name is required";
    if (!form.service) errs.service = "Select a service";
    if (!form.budget) errs.budget = "Select your budget range";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        business_name: form.business_name.trim(),
        phone: `+91${form.phone.replace(/\D/g, "").slice(-10)}`,
        ...getUtm(),
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lead_form_submit", form_id: formId });
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div id={formId} data-testid={`${prefix}-lead-form-card`} className="glass-card rounded-3xl p-6 sm:p-7 scroll-mt-24">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-mono2 uppercase tracking-[0.25em] text-cyan-400 font-semibold">
          Free Strategy Call
        </span>
        <span className="flex items-center gap-1.5 text-[10px] font-medium text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Slots open this week
        </span>
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight">
        Get Your Free Growth Plan
      </h3>
      <p className="text-xs text-slate-400 mt-1 mb-5">
        Fill this in — a senior strategist calls you back within 24 hours.
      </p>
      <form onSubmit={submit} noValidate data-testid={`${prefix}-lead-form`}>
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={set("website")}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] -top-[9999px] h-0 w-0 opacity-0"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field label="Full Name *" error={errors.full_name} testId={`${prefix}-error-name`}>
            <input
              data-testid={`${prefix}-input-name`}
              className={`field-input ${errors.full_name ? "field-error" : ""}`}
              placeholder="Rahul Sharma"
              value={form.full_name}
              onChange={set("full_name")}
            />
          </Field>
          <Field label="Email *" error={errors.email} testId={`${prefix}-error-email`}>
            <input
              data-testid={`${prefix}-input-email`}
              type="email"
              className={`field-input ${errors.email ? "field-error" : ""}`}
              placeholder="you@business.com"
              value={form.email}
              onChange={set("email")}
            />
          </Field>
          <Field label="Phone / WhatsApp *" error={errors.phone} testId={`${prefix}-error-phone`}>
            <div className={`flex items-stretch field-input !p-0 overflow-hidden ${errors.phone ? "field-error" : ""}`}>
              <span className="flex items-center px-3 text-xs font-semibold text-slate-300 bg-white/5 border-r border-white/10">
                +91
              </span>
              <input
                data-testid={`${prefix}-input-phone`}
                type="tel"
                inputMode="numeric"
                className="flex-1 bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-slate-500"
                placeholder="98765 43210"
                value={form.phone}
                onChange={set("phone")}
              />
            </div>
          </Field>
          <Field label="Business Name *" error={errors.business_name} testId={`${prefix}-error-business`}>
            <input
              data-testid={`${prefix}-input-business`}
              className={`field-input ${errors.business_name ? "field-error" : ""}`}
              placeholder="Sharma Realty"
              value={form.business_name}
              onChange={set("business_name")}
            />
          </Field>
          <Field label="Service Interested In *" error={errors.service} testId={`${prefix}-error-service`}>
            <div className="relative">
              <select
                data-testid={`${prefix}-select-service`}
                className={`field-input pr-9 ${errors.service ? "field-error" : ""} ${!form.service ? "text-slate-500" : ""}`}
                value={form.service}
                onChange={set("service")}
              >
                <option value="" disabled>
                  Choose a service
                </option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            </div>
          </Field>
          <Field label="Monthly Ad Budget *" error={errors.budget} testId={`${prefix}-error-budget`}>
            <div className="relative">
              <select
                data-testid={`${prefix}-select-budget`}
                className={`field-input pr-9 ${errors.budget ? "field-error" : ""} ${!form.budget ? "text-slate-500" : ""}`}
                value={form.budget}
                onChange={set("budget")}
              >
                <option value="" disabled>
                  Select range
                </option>
                {BUDGETS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            </div>
          </Field>
        </div>
        <div className="mt-3.5">
          <Field label="Anything we should know? (optional)">
            <textarea
              data-testid={`${prefix}-input-message`}
              rows={2}
              className="field-input resize-none"
              placeholder="Tell us about your goals, current ads, or challenges..."
              value={form.message}
              onChange={set("message")}
            />
          </Field>
        </div>
        <button
          data-testid={`${prefix}-submit-button`}
          type="submit"
          disabled={submitting}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 gradient-bg text-white font-semibold text-sm sm:text-base px-6 py-3.5 rounded-xl hover:brightness-110 hover:shadow-[0_0_32px_rgba(99,102,241,0.5)] transition-[box-shadow,filter] duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 size={17} className="animate-spin" /> Sending...
            </>
          ) : (
            <>
              Claim My Free Strategy Call <ArrowRight size={17} />
            </>
          )}
        </button>
        <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
          <ShieldCheck size={13} className="text-emerald-400" />
          Your details are safe. No spam, no sharing — ever.
        </p>
      </form>
    </div>
  );
};
